import { BrokenImage, ContentCopy, List } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonProps,
  IconButton,
  IconButtonProps,
  Tooltip,
  TooltipProps,
  Typography,
} from '@mui/material';
import { FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router';

export const TooltipIconButton: FC<
  PropsWithChildren<{
    tooltip: string;
    onClick: () => void;
    iconButtonProps?: IconButtonProps;
    tooltipProps?: Partial<Omit<TooltipProps, 'title' | 'children'>>;
  }>
> = ({ children, tooltip, onClick, iconButtonProps, tooltipProps }) => {
  return (
    <Tooltip title={tooltip} {...tooltipProps}>
      <span>
        <IconButton
          aria-label={tooltip}
          {...iconButtonProps}
          onClick={iconButtonProps?.disabled ? undefined : onClick}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const TooltipSquareIconButton: FC<
  PropsWithChildren<{
    tooltip: string;
    onClick: () => void;
    iconButtonProps?: ButtonProps;
    variant?: 'default' | 'error';
  }>
> = ({ children, tooltip, onClick, iconButtonProps, variant = 'default' }) => {
  const isError = variant === 'error';
  iconButtonProps = {
    ...iconButtonProps,
    sx: {
      color: isError ? 'error.contrastText' : 'text.primary',
      backgroundColor: isError ? 'error.main' : 'action.hover',
      borderRadius: '4px',
      minWidth: '32px',
      '&:hover': { backgroundColor: isError ? 'error.dark' : 'action.selected' },
      ...iconButtonProps?.sx,
    },
  };
  return (
    <Tooltip title={tooltip}>
      <span>
        <Button aria-label={tooltip} {...iconButtonProps} onClick={onClick}>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
};

export const TooltipIconButtonLink: FC<
  PropsWithChildren<{
    tooltip: string;
    link: string;
    iconButtonProps?: IconButtonProps;
    openInNewTab?: true;
    onClick?: (e: React.MouseEvent) => void;
    ariaLabel?: string;
  }>
> = ({ children, tooltip, iconButtonProps, link, openInNewTab, onClick, ariaLabel = tooltip }) => (
  <Tooltip title={tooltip}>
    <Link to={link} {...(openInNewTab ? { target: '_blank' } : undefined)} onClick={onClick}>
      <IconButton {...iconButtonProps} aria-label={ariaLabel}>
        {children}
      </IconButton>
    </Link>
  </Tooltip>
);

export const CopyIconButton: FC<
  Omit<IconButtonProps, 'onClick' | 'disabled' | 'children' | 'aria-label'> & {
    value?: string | number | null;
    enabledTitle?: string | ((v: string) => string);
    disabledTitle?: string;
    ariaLabel?: string;
    onCopied?: (value: string) => void;
    icon?: ReactNode;
  }
> = ({
  value,
  enabledTitle = (v) => `Copy "${v}"`,
  disabledTitle = 'Nothing to copy',
  ariaLabel = 'Copy to clipboard',
  onCopied,
  icon,
  size = 'small',
  ...props
}) => {
  const stringValue = value == null ? '' : String(value).trim();
  const canCopy = stringValue.length > 0;

  const handleCopy = async () => {
    if (!canCopy) return;
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(stringValue);
    }
    onCopied?.(stringValue);
  };

  const title = canCopy
    ? typeof enabledTitle === 'function'
      ? enabledTitle(stringValue)
      : enabledTitle
    : disabledTitle;

  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size={size}
          aria-label={ariaLabel}
          disabled={!canCopy}
          onClick={handleCopy}
          {...props}
        >
          {icon ?? <ContentCopy fontSize="inherit" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const InfoListTooltip: FC<{
  options: { name: string }[];
  label: string;
  isVisible?: boolean;
}> = ({ options, label, isVisible = true }) => {
  if (!isVisible || options.length === 0) return null;

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight="bold">
            {label}:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {options.map((opt, idx) => (
              <li key={idx}>{opt.name}</li>
            ))}
          </Box>
        </Box>
      }
    >
      <List fontSize="small" sx={{ color: 'action.active', cursor: 'help' }} />
    </Tooltip>
  );
};

export const ImageTooltip: FC<{
  url: string;
  urlPattern: RegExp;
  errorMessage: string;
  children: React.ReactElement;
}> = ({ url, urlPattern, errorMessage, children }) => {
  const [error, setError] = useState(false);
  useEffect(() => setError(false), [url]);
  if (!url || !urlPattern.test(url)) return children;
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ p: 0.5 }}>
          {error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BrokenImage fontSize="small" sx={{ color: 'error.light' }} />
              <Typography variant="caption" sx={{ color: 'error.light' }}>
                {errorMessage}
              </Typography>
            </Box>
          ) : (
            <img
              src={url}
              alt="MMS preview"
              style={{ maxWidth: 240, maxHeight: 240, borderRadius: 4, display: 'block' }}
              onError={() => setError(true)}
            />
          )}
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};
